import * as React from 'react';
import {Props} from './model';
import {StyleSheet, View} from 'react-native';
import {useStyles} from '../../hooks/styles';
import {useTheme} from '../../theme';
import {DeviceSize, maxDesktopContentFooterWidth, useAlign} from '../../theme/helpers';
import {Copywrite} from './components/copywrite';
import {FooterGroup} from './components/group';
import {images} from '../../../assets/icons';
import {AppleStore, GooglePlay, HuaweiStore} from '../../../assets/images';
import {Link} from '../link';
import {Image} from '../image';
import {Text} from '../text';
import {useIsDesktop} from '../../hooks/useIsDesktop';
import {formatPhone, openExternalPhone} from '../../pipes';
import {useGoToApp} from '../gotoApp/index.web';

export default ({content}: Props) => {
  const align = useAlign();
  const theme = useTheme();
  const isDesktop = useIsDesktop();
  const goToAppData = useGoToApp();
  const styles = useStyles(
    ({mediaOnly, mediaExclude}) => ({
      container: {
        backgroundColor: theme.footer.bgColor,
      },
      footerContainer: {
        alignSelf: 'center',
        maxWidth: maxDesktopContentFooterWidth,
        width: '100%',
        position: 'relative',
        ...mediaOnly(DeviceSize.desktop, {paddingHorizontal: 24}),
      },
      containerTop: {
        ...mediaOnly(
          DeviceSize.desktop,
          {
            flexDirection: 'row',
            flexWrap: 'wrap',
            paddingBottom: 32,
            paddingTop: 40,
          },
          {
            paddingHorizontal: 30,
            paddingTop: 26,
          },
        ),
      },
      containerBottom: {
        ...mediaOnly(
          DeviceSize.desktop,
          {paddingTop: 16, paddingBottom: 18},
          {paddingTop: 13, paddingBottom: goToAppData.isVisible ? 17 : 34, paddingHorizontal: 16},
        ),
        borderTopWidth: 1,
        borderTopColor: theme.footer.bottomLineColor,
        borderStyle: 'solid',
        alignItems: 'center',
      },
      copywriteText: {
        textAlign: 'center',
      },
      groupText: {
        ...theme.fonts.body,
        color: theme.footer.textColor,
      },
      contactsTitle: {
        ...theme.fonts.h5,
        color: theme.footer.textColor,
      },
      otherTitle: {
        ...mediaOnly(DeviceSize.desktop, theme.fonts.h5, theme.fonts.h6),
        color: theme.footer.textColor,
      },
      externalEKJ: {
        flexDirection: 'row',
      },
      externalEKJSvg: {
        alignSelf: 'center',
        marginRight: 15,
      },
      externalEKJText: {
        ...theme.fonts.body,
        ...mediaOnly(DeviceSize.desktop, {color: theme.color.primaryColor}, {color: theme.color.textColor}),
      },
      group: {
        ...mediaOnly(DeviceSize.desktop, {flexBasis: '25%', paddingRight: 16}, {marginBottom: 20}),
      },
      groupHotline: mediaOnly(
        DeviceSize.desktop,
        {
          flexBasis: '22%',
        },
        {marginBottom: 32},
      ),
      groupEkj: mediaOnly(
        DeviceSize.desktop,
        {
          flexBasis: '22%',
        },
        {marginBottom: 20},
      ),
      groupHotlineTitleStyle: {marginBottom: isDesktop ? 8 : 4},
      groupUnionNumber: mediaOnly(
        DeviceSize.desktop,
        {
          flexBasis: '27.5%',
          paddingRight: 80,
        },
        {marginBottom: 15},
      ),
      groupSocials: mediaOnly(
        DeviceSize.desktop,
        {
          flexBasis: '31.5%',
        },
        {marginBottom: 22},
      ),
      groupMarkets: {
        ...mediaOnly(DeviceSize.desktop, {flexBasis: '45%', paddingRight: 16, marginBottom: 15}, {marginBottom: 20}),
        ...mediaExclude(DeviceSize.desktop, {marginBottom: 18}),
      },
      groupLast: {
        ...mediaOnly(DeviceSize.desktop, {marginTop: -3}, {marginBottom: 18}),
      },
      groupOtherTitle: {
        marginBottom: isDesktop ? 12 : 10,
      },
      socials: {
        flexDirection: 'row',
        alignItems: 'center',
      },
      markets: {
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        maxWidth: 438,
        justifyContent: 'flex-start',
        marginLeft: -10,
      },
      market: {
        marginLeft: 10,
        ...mediaOnly(DeviceSize.desktop, {marginBottom: 8}, {marginBottom: 9}),
      },
      social: {
        marginRight: 23,
        color: theme.footer.textColor,
      },
      socialIcon: {
        color: theme.footer.textColor,
      } as any,
      socialNumberDescription: {
        maxWidth: 245,
      },
    }),
    [goToAppData.isVisible],
  );

  const marketIconSize: {width: number; height: number} = isDesktop
    ? {width: 139, height: 41}
    : {width: 130, height: 39};

  const dataFooter = {
    dataSet: {
      class: 'footer',
    },
  };

  let isMarketsEnabled = false;

  for (const [key, value] of Object.entries(content.footer.stores)) {
    isMarketsEnabled = !!value;
  }

  return React.useMemo(() => {
    return (
      <View style={styles.container}>
        <View style={styles.footerContainer} {...dataFooter}>
          <View style={styles.containerTop}>
            <FooterGroup
              style={[styles.group, styles.groupHotline]}
              titleStyle={styles.groupHotlineTitleStyle}
              title={
                <Link testID='phone-eskmo-contact-support-link-id' dataSetClass='footer-link' external>
                  <Text
                    onPress={() => openExternalPhone(content.eskmoContacts.supportPhone)}
                    style={styles.contactsTitle}>
                    {formatPhone(content.eskmoContacts.supportPhone)}
                  </Text>
                </Link>
              }>
              <Text testID='hotline-text-id' style={styles.groupText}>
                {content.footer.telephones.hotlineText}
              </Text>
            </FooterGroup>
            <FooterGroup
              style={[styles.group, styles.groupSocials]}
              // eslint-disable-next-line react-native/no-inline-styles
              titleStyle={[styles.groupOtherTitle, {marginBottom: isDesktop ? 18 : 12}]}
              childStyle={styles.socials}
              title={
                <Text testID='we-in-social-meida-id' style={styles.otherTitle}>
                  Мы в соцсетях
                </Text>
              }>
              {/* {content.footer.socials.facebook ? (
                <Link
                  testID='social-network-fb-app-link-id'
                  accessibilityLabel='facebook'
                  dataSetClass='link-social'
                  href={content.footer.socials.facebook}
                  external={true}
                  style={styles.social}>
                  <images.SocialFacebook width={32} height={32} color={styles.socialIcon.color} />
                </Link>
              ) : null}
              {content.footer.socials.instagram ? (
                <Link
                  testID='social-network-inst-app-link-id'
                  accessibilityLabel='instagram'
                  dataSetClass='link-social'
                  href={content.footer.socials.instagram}
                  external={true}
                  style={styles.social}>
                  <images.SocialInstagram width={29} height={29} color={styles.socialIcon.color} />
                </Link>
              ) : null} */}
              {content.footer.socials.vk ? (
                <Link
                  testID='social-network-vk-app-link-id'
                  accessibilityLabel='вконтакте'
                  dataSetClass='link-social'
                  href={content.footer.socials.vk}
                  external={true}
                  style={styles.social}>
                  <images.SocialVk width={42} height={24} color={styles.socialIcon.color} />
                </Link>
              ) : null}
              {content.footer.socials.ok ? (
                <Link
                  testID='social-network-ok-app-link-id'
                  accessibilityLabel='одноклассники'
                  dataSetClass='link-social'
                  href={content.footer.socials.ok}
                  external={true}
                  style={styles.social}>
                  <images.SocialOk width={14} height={24} color={styles.socialIcon.color} />
                </Link>
              ) : null}
            </FooterGroup>
            {isMarketsEnabled ? (
              <FooterGroup
                style={[styles.group, styles.groupMarkets]}
                titleStyle={styles.groupOtherTitle}
                childStyle={styles.markets}
                title={
                  <Text testID='official-app-id' style={styles.otherTitle}>
                    Официальное приложение
                  </Text>
                }>
                {content.footer.stores.appStore ? (
                  <Link
                    testID='ios-app-link-id'
                    accessibilityLabel='ios приложение'
                    dataSetClass='footer-link'
                    href={content.footer.stores.appStore}
                    external
                    style={styles.market}>
                    <Image
                      source={AppleStore as any}
                      width={align(marketIconSize.width)}
                      height={align(marketIconSize.height)}
                    />
                  </Link>
                ) : null}
                {content.footer.stores.googlePlay ? (
                  <Link
                    testID='android-app-link-id'
                    accessibilityLabel='android приложение'
                    dataSetClass='footer-link'
                    href={content.footer.stores.googlePlay}
                    external
                    style={styles.market}>
                    <Image
                      source={GooglePlay as any}
                      width={align(marketIconSize.width)}
                      height={align(marketIconSize.height)}
                    />
                  </Link>
                ) : null}
                {content.footer.stores.appGallery ? (
                  <Link
                    testID='huawei-app-link-id'
                    accessibilityLabel='huawei приложение'
                    dataSetClass='footer-link'
                    href={content.footer.stores.appGallery}
                    external
                    style={styles.market}>
                    <Image
                      source={HuaweiStore as any}
                      width={align(marketIconSize.width)}
                      height={align(marketIconSize.height)}
                    />
                  </Link>
                ) : null}
              </FooterGroup>
            ) : null}
            <FooterGroup style={[styles.group, styles.groupLast, styles.groupEkj]} title={null}>
              <View style={[styles.groupText]}>
                <Link
                  testID='external-website-link-id'
                  dataSetClass='footer-link'
                  style={styles.externalEKJ}
                  external={true}
                  href={content.eskmoContacts.website}>
                  <View style={styles.externalEKJSvg}>
                    <images.Up color={StyleSheet.flatten(styles.externalEKJText).color} width={12} height={12} />
                  </View>
                  <Text testID='esk-portal-id' style={styles.externalEKJText}>
                    Портал ЕКЖ
                  </Text>
                </Link>
              </View>
            </FooterGroup>
            <FooterGroup style={[styles.group, styles.groupLast, styles.groupUnionNumber]} title={null}>
              <View style={[styles.groupText]}>
                <Link testID='politics-link-id' dataSetClass='footer-link' external={true} href={content.docs.politics}>
                  <Text style={styles.groupText}>Политика конфиденциальности</Text>
                </Link>
              </View>
            </FooterGroup>
          </View>
          <View style={styles.containerBottom}>
            <Copywrite textStyle={styles.copywriteText} />
          </View>
        </View>
      </View>
    );
  }, [isDesktop, theme]);
};
